import { getLabelsetBelongingToGroup } from '../../shared_components/utils/FirebaseLabelsetsUtils';

/**
 * Reading the material classes off a recipe ingredient.
 *
 * An ingredient declares its material through `variants`: a list of attribute sets it matches,
 * each holding exactly one material class plus any number of further attributes (form, contents,
 * ranges). An observation matches the ingredient as soon as *one* variant matches, so an
 * ingredient stands for as many material classes as it has variants — see
 * `assignIngredientToObservation.py` in the backend for the matching side of the same contract.
 *
 * The key holding the material class is client-specific: it is whichever labelset sits in the
 * `materials` group (`fiber_materials`, `plastic_materials`, ...), so it is resolved per variant
 * rather than hardcoded. Every other attribute is a further constraint the buttons cannot express
 * and is therefore ignored here.
 */

const MATERIALS_GROUP_ID = 'materials';

/**
 * Collect the material classes an ingredient stands for, one per variant.
 *
 * Ids are returned as strings, deduplicated, in variant order. Variants without a material
 * class are skipped: a button can only ever match a concrete class, so a blank one narrows nothing.
 *
 * @param {Object|null} ingredient - Ingredient document (carries `variants`).
 * @param {Object|null} globalLabelsets - Labelset definitions keyed by labelset id, used to find
 *   which attribute key belongs to the `materials` group.
 * @returns {Array<string>} Material class label ids.
 */
export function getIngredientMaterialClasses(ingredient, globalLabelsets) {
  if (!Array.isArray(ingredient?.variants) || !globalLabelsets) return [];

  const materialClasses = ingredient.variants.map((variant) => {
    const attributes = variant?.material_attributes;
    if (!attributes || typeof attributes !== 'object') return null;

    const materialKey = getLabelsetBelongingToGroup(Object.keys(attributes), globalLabelsets, MATERIALS_GROUP_ID);
    const materialClass = materialKey ? attributes[materialKey] : null;

    return materialClass === null || materialClass === undefined || materialClass === '' ? null : String(materialClass);
  });

  return [...new Set(materialClasses.filter(Boolean))];
}
